/**
 * Data-rich card primitives — gauges, bars, stat chips, sparklines, insights.
 *
 * All are pure SVG/CSS (zero external deps), theme-aware (semantic tokens),
 * accessible (roles + labels), and reduced-motion-safe. Designed to make grid
 * cards feel like a dashboard: a lead-score `Gauge`, a lifecycle `ProgressBar`,
 * `StatChip`s for counts/deltas, an inline `Sparkline`, and an `InsightBadge`
 * for small computed/AI notes.
 *
 *   import { Gauge, ProgressBar, StatChip, Sparkline, InsightBadge } from '@/ui';
 */

import { type ReactNode, useEffect, useState } from 'react';

import { type AccentTone } from './Card';

// Tone → linear-gradient endpoints (fills) and solid text colour.
const BAR_GRADIENT: Record<AccentTone, string> = {
  primary: 'from-primary to-accent',
  accent: 'from-accent to-primary',
  success: 'from-success to-success/70',
  warning: 'from-warning to-warning/70',
  danger: 'from-danger to-danger/70',
};
const TEXT_TONE: Record<AccentTone, string> = {
  primary: 'text-primary',
  accent: 'text-accent',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
};

/** Higher-is-better tone ramp for a 0–100 score. */
function scoreTone(pct: number): AccentTone {
  if (pct >= 67) return 'success';
  if (pct >= 34) return 'warning';
  return 'danger';
}

/** Respects `prefers-reduced-motion`; re-evaluates on change. */
function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const on = () => setReduced(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return reduced;
}

// ---------------------------------------------------------------------------
// Gauge — radial 0–100 meter.
// ---------------------------------------------------------------------------

export interface GaugeProps {
  /** Current value (clamped to `[0, max]`). */
  value: number;
  max?: number;
  /** Diameter in px. */
  size?: number;
  /** Fixed tone; when omitted a higher-is-better ramp is derived from value. */
  tone?: AccentTone;
  /** Big centred label (defaults to the rounded percentage). */
  centerLabel?: ReactNode;
  /** Small caption under the value. */
  caption?: ReactNode;
  /** Accessible name (falls back to "<pct> pour cent"). */
  ariaLabel?: string;
  className?: string;
}

/**
 * Radial progress dial. Renders a token-themed track + a tone-coloured arc that
 * grows in on mount (unless reduced motion). Accessible as a `meter`.
 */
export function Gauge({
  value,
  max = 100,
  size = 72,
  tone,
  centerLabel,
  caption,
  ariaLabel,
  className = '',
}: GaugeProps) {
  const reduced = useReducedMotion();
  const safeMax = max <= 0 ? 1 : max;
  const clamped = Math.max(0, Math.min(value, safeMax));
  const pct = Math.round((clamped / safeMax) * 100);
  const resolvedTone = tone ?? scoreTone(pct);

  const stroke = Math.max(5, Math.round(size / 11));
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;

  // Animate the arc from empty → value on mount.
  const [shown, setShown] = useState(reduced ? pct : 0);
  useEffect(() => {
    if (reduced) {
      setShown(pct);
      return undefined;
    }
    const id = requestAnimationFrame(() => setShown(pct));
    return () => cancelAnimationFrame(id);
  }, [pct, reduced]);

  const offset = circ * (1 - shown / 100);

  return (
    <div
      className={`relative inline-flex shrink-0 items-center justify-center ${className}`.trim()}
      style={{ width: size, height: size }}
      role="meter"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={safeMax}
      aria-label={ariaLabel ?? `${pct} pour cent`}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          className="stroke-surface-2"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          className={`${TEXT_TONE[resolvedTone]} stroke-current transition-[stroke-dashoffset] duration-700 ease-out motion-reduce:transition-none`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-sm font-bold tabular-nums ${TEXT_TONE[resolvedTone]}`}>
          {centerLabel ?? `${pct}%`}
        </span>
        {caption != null && (
          <span className="mt-0.5 text-[10px] leading-none text-muted">{caption}</span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProgressBar — linear labelled meter.
// ---------------------------------------------------------------------------

export interface ProgressBarProps {
  value: number;
  max?: number;
  label?: ReactNode;
  tone?: AccentTone;
  /** Show the percentage on the right of the label row. */
  showValue?: boolean;
  /** Bar thickness. */
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * Labelled linear progress bar — accessible `progressbar`, tone-coloured
 * gradient fill that grows in (reduced-motion safe). Track uses `surface-2`.
 */
export function ProgressBar({
  value,
  max = 100,
  label,
  tone = 'primary',
  showValue = false,
  size = 'md',
  className = '',
}: ProgressBarProps) {
  const safeMax = max <= 0 ? 1 : max;
  const clamped = Math.max(0, Math.min(value, safeMax));
  const pct = Math.round((clamped / safeMax) * 100);
  return (
    <div className={className}>
      {(label != null || showValue) && (
        <div className="mb-1 flex items-center justify-between gap-2 text-xs text-muted">
          {label != null ? <span className="min-w-0 truncate">{label}</span> : <span />}
          {showValue && (
            <span className="shrink-0 font-semibold tabular-nums text-foreground">{pct}%</span>
          )}
        </div>
      )}
      <div
        role="progressbar"
        aria-valuenow={clamped}
        aria-valuemin={0}
        aria-valuemax={safeMax}
        aria-label={typeof label === 'string' ? label : 'Progression'}
        className={`w-full overflow-hidden rounded-full bg-surface-2 ${
          size === 'sm' ? 'h-1.5' : 'h-2.5'
        }`}
      >
        <div
          className={`um-meter-fill h-full rounded-full bg-gradient-to-r ${BAR_GRADIENT[tone]}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// StatChip — value + label with an optional trend delta.
// ---------------------------------------------------------------------------

export interface StatChipProps {
  label: ReactNode;
  value: ReactNode;
  /** Signed delta; sign drives the arrow + colour (positive = success). */
  delta?: number;
  /** Text shown next to the delta arrow (defaults to the delta number). */
  deltaLabel?: ReactNode;
  /** Higher delta is bad (e.g. churn) → invert the colour mapping. */
  invertDelta?: boolean;
  icon?: ReactNode;
  className?: string;
}

/** Compact KPI tile: a bold value, a muted label, and an optional trend. */
export function StatChip({
  label,
  value,
  delta,
  deltaLabel,
  invertDelta = false,
  icon,
  className = '',
}: StatChipProps) {
  const hasDelta = typeof delta === 'number' && Number.isFinite(delta) && delta !== 0;
  const good = hasDelta ? (invertDelta ? delta! < 0 : delta! > 0) : false;
  const up = hasDelta ? delta! > 0 : false;
  return (
    <div
      className={`flex min-w-0 flex-col gap-0.5 rounded-token border border-border bg-surface-2/50 px-2.5 py-2 ${className}`.trim()}
    >
      <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted">
        {icon != null && <span className="shrink-0 text-muted">{icon}</span>}
        <span className="min-w-0 truncate">{label}</span>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="truncate text-lg font-bold tabular-nums text-foreground">{value}</span>
        {hasDelta && (
          <span
            className={`inline-flex items-center gap-0.5 text-xs font-semibold tabular-nums ${
              good ? 'text-success' : 'text-danger'
            }`}
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              className={`h-3 w-3 ${up ? '' : 'rotate-180'}`}
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 19V5M6 11l6-6 6 6" />
            </svg>
            {deltaLabel ?? Math.abs(delta!)}
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sparkline — tiny inline trend chart.
// ---------------------------------------------------------------------------

export interface SparklineProps {
  data: number[];
  width?: number;
  height?: number;
  tone?: AccentTone;
  /** Fill the area under the line with a faint tone gradient. */
  area?: boolean;
  ariaLabel?: string;
  className?: string;
}

/**
 * Minimal SVG sparkline from a numeric series. Degrades gracefully for empty /
 * single-point data (renders a flat baseline). Decorative but announced via a
 * summarising `aria-label`.
 */
export function Sparkline({
  data,
  width = 72,
  height = 22,
  tone = 'primary',
  area = false,
  ariaLabel,
  className = '',
}: SparklineProps) {
  const pad = 2;
  const w = width;
  const h = height;
  const pts = data.filter((n) => Number.isFinite(n));
  const min = pts.length ? Math.min(...pts) : 0;
  const max = pts.length ? Math.max(...pts) : 1;
  const span = max - min || 1;
  const stepX = pts.length > 1 ? (w - pad * 2) / (pts.length - 1) : 0;
  const y = (v: number) => h - pad - ((v - min) / span) * (h - pad * 2);
  const coords = pts.map((v, i) => [pad + i * stepX, y(v)] as const);
  // A single point renders as a flat mid-line so the chart never disappears.
  const line =
    coords.length >= 2
      ? coords.map(([x, yy], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${yy.toFixed(1)}`).join(' ')
      : `M${pad} ${(h / 2).toFixed(1)} L${(w - pad).toFixed(1)} ${(h / 2).toFixed(1)}`;
  const areaPath =
    coords.length >= 2
      ? `${line} L${(pad + (coords.length - 1) * stepX).toFixed(1)} ${h - pad} L${pad} ${h - pad} Z`
      : '';

  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      className={`overflow-visible ${TEXT_TONE[tone]} ${className}`.trim()}
      role="img"
      aria-label={ariaLabel ?? 'Tendance'}
      preserveAspectRatio="none"
    >
      {area && areaPath && (
        <path d={areaPath} className="fill-current opacity-10" stroke="none" />
      )}
      <path
        d={line}
        fill="none"
        className="stroke-current"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// InsightBadge — small computed / AI note pill.
// ---------------------------------------------------------------------------

export type InsightTone = 'info' | 'success' | 'warning' | 'danger';

export interface InsightBadgeProps {
  children: ReactNode;
  tone?: InsightTone;
  /** Custom leading icon; a tone-appropriate default is used when omitted. */
  icon?: ReactNode;
  className?: string;
}

const INSIGHT_STYLES: Record<InsightTone, string> = {
  info: 'border-primary/30 bg-primary/10 text-primary',
  success: 'border-success/30 bg-success/10 text-success',
  warning: 'border-warning/30 bg-warning/10 text-warning',
  danger: 'border-danger/30 bg-danger/10 text-danger',
};

const SparkIcon = (
  <svg aria-hidden="true" viewBox="0 0 24 24" className="h-3 w-3 shrink-0" fill="currentColor">
    <path d="M12 2l1.6 5.4L19 9l-5.4 1.6L12 16l-1.6-5.4L5 9l5.4-1.6z" />
  </svg>
);

/** Small pill for a computed/AI insight (e.g. "Aucune activité 30 j"). */
export function InsightBadge({ children, tone = 'info', icon, className = '' }: InsightBadgeProps) {
  return (
    <span
      className={`inline-flex max-w-full items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${INSIGHT_STYLES[tone]} ${className}`.trim()}
    >
      {icon ?? SparkIcon}
      <span className="min-w-0 truncate">{children}</span>
    </span>
  );
}
